import { IPromocode } from "@/api/promocodes/promocodes";
import { Currencies } from "@/constants/currency";
import { PSBErrors } from "@/constants/errors/product-slot-booking";
import { IReceiptPayment } from "@/interfaces/receipts";
import { IBookingPayment } from "@/interfaces/store/IBooking";
import i18n from "@/lang";
// Simplified type alias to avoid TS2589 (excessively deep type instantiation)
// caused by vue-i18n v11's complex overloaded generics on t()
const t: (key: string, ...args: unknown[]) => string = (i18n.global as any).t;
import { LogService } from "@/services/logging";
import BookingModule from "@/store/modules/booking";
import CashFloatModule from "@/store/modules/cash-float";
import DeviceModule from "@/store/modules/device";
import POSConfigModule from "@/store/modules/pos-config";
import {
  AccountingState,
  IAccounting,
  IAccountingInfo,
  PaymentMethods,
  PaymentTool,
  PaymentToolMethod,
  PromocodeCampaignType,
  TransactionTypes,
} from "@/types/db/accounting";
import { IAccountingEntry } from "@/types/db/accounting-entry";
import { ITax } from "@/types/db/product-category";
import {
  IProductSlotBooking,
  BookingType,
} from "@/types/db/product-slot-booking";
import {
  BookingStatus,
  IProductSlotState,
  PaymentStatus,
} from "@/types/db/product-slot-state";
import dayjs from "dayjs";
import { cloneDeep, reject } from "lodash";
import { generateID } from "../helpers/app";
import { nowDB } from "../helpers/dates";
import { getTaxValue } from "../helpers/tax";
import { getpssExtension } from "./booking";
import AuthModule from "@/store/modules/auth";
import {
  createCashRoundingAccounting,
  createCashRoundingRefundAccounting,
  excludedTaxByPss,
  isPSSPaid,
  paymentByPss,
  pssUnpaidValue,
  refundsByPss,
  restToPay,
  totalDiscountsByPss,
} from "./new-accountings";
import FiskalyModule from "@/store/modules/fiskaly";
import {
  isMultipleOf5,
  roundToNearestMultipleOf5,
  sumArray,
} from "../helpers/common";
import { getPSSValue } from "../helpers/product-slot-state";
import { isBookingInDexie } from "../helpers/booking";
import {
  PaymentMethodTypes,
  PaymentMethodTypeToPaymentTool,
} from "@/types/db/pos-config";

/**
 * create Accounting, Accounting entries, and update PSS payment data
 * BELOW IS NOT DONE ANYMORE AFTER PROMOCODE REWRITE
 * It created discountAccounting if we have discount but we don't have its accounting
 * It creates promocodeDiscount accounting if we need to (case of promocodeDiscount && no promocode accounting)
 */
export async function createPosAccounting(
  booking: IProductSlotBooking,
  paymentMethod: string = PaymentMethods.METHOD_POS_UNKNOWN,
  // accountingInfo?: IAccountingInfo,
  bookingPayment?: IBookingPayment
): Promise<IAccounting> {
  console.log("-------- hi from createPosAccounting");
  LogService.info(
    `Creating accounting for booking: ${booking.uuid} and payment method: ${paymentMethod}`
  );
  if (
    !Object.values(PaymentMethods).includes(paymentMethod as PaymentMethods)
  ) {
    LogService.info(
      `Payment method not supported, booking: ${booking.uuid}, payment method: ${paymentMethod}`
    );
    throw new Error("Payment method not supported");
  }

  // booking.goerToken = crypto.randomBytes(48).toString('base64url');;
  // booking.organiserToken = crypto.randomBytes(48).toString('base64url');;

  let uuid = generateID();
  let creationTime = dayjs().toISOString();
  const groupId = String(POSConfigModule.groupId);

  const accountingState = AccountingState.Pending;

  const transactionType =
    paymentMethod === PaymentMethods.METHODS_DISCOUNT
      ? TransactionTypes.TRANSACTION_TYPE_DISCOUNT
      : TransactionTypes.TRANSACTION_TYPE_PAYMENT;

  // link the transaction to the new accounting
  const transaction =
    FiskalyModule.transactionsInProgress[String(booking.uuid)];

  const accounting: IAccounting = {
    _id: uuid,
    uuid: uuid,
    orderSessionId: String(BookingModule.currentOrderSessionId),
    state: accountingState,
    transactionType: transactionType,
    paymentMethod: paymentMethod as PaymentMethods,
    bookingUuid: booking.uuid,
    promoCodeUuid: null,
    createdAt: creationTime,
    updatedAt: creationTime,
    message: creationTime + " :: accounting was created.",
    isDeposit: false,
    currency: booking.currency,
    accountingEntriesList: [],
    cashRegisterId: DeviceModule.cashRegisterId,
    channel: "Smeetz POS",
    merchantReference: "",
    CfUid: CashFloatModule.cashFloat?.uid,
    createdBy: AuthModule.cognitoId,
    fiskalyTransactionUuid: transaction ? transaction.uuid : null,
  };

  if (
    (paymentMethod as PaymentMethods) === PaymentMethods.METHOD_POS_CASH &&
    booking.uuid
  ) {
    const selectedCashCurrency = BookingModule.selectedCashCurrency;

    const splitPaymentAmount =
      BookingModule.splitAmount?.splitPaymentAmount || 0;

    const splitAmountProvided =
      BookingModule.splitAmount?.splitAmountProvided || 0;

    let providedCashBill = splitPaymentAmount;
    if (selectedCashCurrency) {
      // if split payment was in secondary currency and was rounded after converting it to CHF
      // we take the amount payed before rounding which is what the buyer actually handed
      // if no rounding was done then amount before rounding would equal the amount after anyway
      providedCashBill =
        splitAmountProvided * selectedCashCurrency.exchangeRate;

      accounting.paymentCurrency = selectedCashCurrency.label;
      accounting.exchangeRate = selectedCashCurrency.exchangeRate;
    }
    let providedCashBillInCents = providedCashBill * 100;
    if (POSConfigModule.requiresCashRounding) {
      // Belgium: the cash actually handed over is always a multiple of 5 cents
      providedCashBillInCents = roundToNearestMultipleOf5(
        providedCashBillInCents
      );
    }
    accounting.providedCashBill = Number(providedCashBillInCents.toFixed(0));

    // always initialize accounting.amountReturned to 0
    accounting.amountReturned = 0;
  }

  if ((paymentMethod as PaymentMethods) === PaymentMethods.METHOD_POS_ADYEN) {
    accounting.merchantReference = `NEW_POS_${groupId}_${accounting.bookingUuid}_${accounting.uuid}`;
  }

  // Belgian cash rounding difference produced while building the entries, in cents
  let cashRoundingDifference = 0;

  // Lets now create accountingEntries for this payment accounting
  // Keep in mind that split payment accounting can have an amount that will not fully cover the pssList
  // Using toFixed() here to round floating-point result
  if (BookingModule.doesSupportSplitPayment && BookingModule.splitAmount) {
    const splitAmount = Math.round(
      (BookingModule.splitAmount?.splitPaymentAmount || 0) * 100
    );

    const splitAmountProvided = Math.round(
      (BookingModule.splitAmount?.splitAmountProvided || 0) * 100
    );
    // console.log(
    //   "&&&&&&&&&&&&&&&&&&&&&&&&& going to create accEntries for splitPayment"
    // );
    // This booking support split payment and the user has entered a split amount
    // Lets create accounting entries based onthe provided amount
    const { accEntries, roundingDifference } =
      await createAccEntriesForSplitAmount(
        splitAmount,
        splitAmountProvided,
        booking,
        String(accounting.uuid),
        (paymentMethod as PaymentMethods) === PaymentMethods.METHOD_POS_CASH
      );
    cashRoundingDifference = roundingDifference;
    const selectedCashCurrency = BookingModule.selectedCashCurrency;
    const exchangeRate = selectedCashCurrency?.exchangeRate || 1;
    const paymentCurrency = selectedCashCurrency?.label || booking.currency;
    accounting.exchangeRate = exchangeRate;
    accounting.paymentCurrency = paymentCurrency;
    accounting.accountingEntriesList = accEntries;
    const accEntriesTotal = sumArray(accEntries.map((ae) => ae.value));
    if (
      paymentCurrency !== booking.currency &&
      accEntriesTotal < Number(splitAmount)
    ) {
      // payments in foreign currency with change to give back
      let changeValue = splitAmount - accEntriesTotal;
      if (booking.currency === Currencies.CHF) {
        // Round change value to a multiple of 5 for CHF transactions
        // as CHF doesn't have coins below 5 cents
        changeValue = roundToNearestMultipleOf5(changeValue);
      }
      const paymentAndChange = {
        changeCurrency: booking.currency,
        changeValue,
        exchangeRate: exchangeRate,
        paymentCurrency: paymentCurrency,
        paymentValue: accEntriesTotal,
      };
      accounting.paymentAndChange = paymentAndChange;
      accounting.amountReturned = changeValue;
    } else if (
      paymentCurrency === booking.currency &&
      accounting.providedCashBill &&
      accEntriesTotal < accounting.providedCashBill
    ) {
      // always give accounting.amountReturned a value even when the booking is paid with the main currency only
      accounting.amountReturned = accounting.providedCashBill - accEntriesTotal;
    }
  } else {
    const selectedCashCurrency = BookingModule.selectedCashCurrency;
    const exchangeRate = selectedCashCurrency?.exchangeRate || 1;
    const accEntries: IAccountingEntry[] = [];
    // We're not a split payment, we create the accountingEntries for all pss as we're used to do
    for (const pss of booking.pssList) {
      // Do nothing if this pss is Completed and we're in EditOrder
      const isEditOrder = await isBookingInDexie(booking.uuid);
      if (isEditOrder && isPSSPaid(pss)) {
        continue;
      }
      // set the booking status and payment status depending on the payment method
      if (paymentMethod === PaymentMethods.METHOD_POS_ADYEN) {
        // We reset paymentStatus and bookingSatus only if the pss is not already paid or cancelled (EditOrder case)
        if (
          pss.bookingStatus !== BookingStatus.Confirmed &&
          pss.bookingStatus !== BookingStatus.Canceled &&
          pss.bookingStatus !== BookingStatus.Abandoned
        )
          pss.bookingStatus = BookingStatus.Pending;
        if (
          pss.paymentStatus !== PaymentStatus.Completed &&
          pss.paymentStatus !== PaymentStatus.Canceled
        )
          pss.paymentStatus = PaymentStatus.Started;
      } else {
        if (
          pss.bookingStatus !== BookingStatus.Canceled &&
          pss.bookingStatus !== BookingStatus.Abandoned
        )
          // pss.bookingStatus = BookingStatus.Confirmed;
          pss.bookingStatus = BookingStatus.Pending;
        if (pss.paymentStatus !== PaymentStatus.Canceled)
          // pss.paymentStatus = PaymentStatus.Completed;
          pss.paymentStatus = PaymentStatus.Started;
      }

      const ttlDiscountsOfPss = totalDiscountsByPss.value(String(pss.uuid));
      const pssValue = getPSSValue(pss);
      const paymentsOfPss = paymentByPss.value(String(pss.uuid));
      const accountingEntryValue = pssValue - ttlDiscountsOfPss - paymentsOfPss;
      // payment currency value rounded to the nearest integer
      const paymentCurrencyValue = Math.round(
        accountingEntryValue * exchangeRate
      );
      // BookingModule.pssFinaLValuesAfterDiscount.find(
      //   (it) => it.pssUuid === pss.uuid
      // )?.pssValue || 0;

      // create AE only if the value is positive
      if (accountingEntryValue > 0) {
        uuid = generateID();
        creationTime = dayjs().toISOString();
        const accountingEntry: IAccountingEntry = {
          _id: uuid,
          uuid: uuid,
          accountingUuid: accounting.uuid,
          orderSessionId: String(BookingModule.currentOrderSessionId),
          productSlotStateUuid: pss.uuid,
          value: accountingEntryValue,
          paymentCurrencyValue: paymentCurrencyValue,
          groupId: pss.productOwnerGroupId,
          bookingStatementVersion: 2,
          createdAt: creationTime,
          updatedAt: creationTime,
          productId: pss.productId /*category.product.id*/,
        };
        if (pss.id) {
          accountingEntry.productSlotStateId = pss.id;
        }
        accEntries.push(accountingEntry);
      }
    }
    accounting.accountingEntriesList = accEntries;
  }

  // Avoid having a payment accounting without accEntries under any circumstance
  // Avoid having an UNKOWN payment
  if (
    accounting.accountingEntriesList.length &&
    paymentMethod !== PaymentMethods.METHOD_POS_UNKNOWN
  ) {
    booking.accountings.push(accounting);
    LogService.info(
      `Accounting created, booking: ${booking.uuid}, accounting: ${accounting.uuid}`,
      {
        params: accounting,
      }
    );

    // Belgium: book the 5-cent cash rounding as its own payment line so the
    // exact total and the rounded cash handed over both stay on the record.
    if (cashRoundingDifference) {
      const roundingAccounting = createCashRoundingAccounting(
        booking,
        cashRoundingDifference
      );
      if (roundingAccounting) {
        booking.accountings.push(roundingAccounting);
        LogService.info(
          `Cash rounding accounting created, booking: ${booking.uuid}, accounting: ${roundingAccounting.uuid}`,
          { params: roundingAccounting }
        );
      }
    }

    // Lets check if this booking has an Invoice accounting so that we convert this booking from a normal booking into an invoice (booking.type: 2)
    const hasInvoice = booking.accountings.some(
      (acc) =>
        acc.paymentMethod === PaymentMethods.METHOD_Swiss_QR_Code_Bank_Transfer
    );
    console.log("hasInvoice");
    console.log(hasInvoice);
    if (hasInvoice) {
      console.log("this booking is an invoice");
      // This booking is an invoice
      booking.type = BookingType.Invoice;
    }
  }
  return accounting;
}

/**
 *
 * @param amount that the buyer provided and that we use to create accountingEntries based on what pss it can cover
 * @param isCash whether the payment is in cash, which is the only case Belgian
 * cash rounding applies to
 * @returns the accEntries, which can be fewer than pssList count depending on the
 * amount provided, plus the Belgian cash-rounding difference in cents (0 when it
 * does not apply). We don't create accEntry for a fully paid pss nor for a
 * cancelled pss
 */
export async function createAccEntriesForSplitAmount(
  amount: number,
  amountProvided: number,
  booking: IProductSlotBooking,
  accountingUuid: string,
  isCash = false
): Promise<{ accEntries: IAccountingEntry[]; roundingDifference: number }> {
  const selectedCashCurrency = BookingModule.selectedCashCurrency;
  const accEntries: IAccountingEntry[] = [];
  let remainingAmount = amount;
  const exchangeRate = selectedCashCurrency?.exchangeRate || 1;
  const reverseExchangeRate = 1 / exchangeRate;
  let newlyAddedAccountingEntriesValue = 0;
  LogService.info(
    `Creating accounting entries for accounting & pssList attached, booking: ${booking.uuid}`,
    { accounting: accountingUuid, pssList: booking.pssList }
  );

  for (let i = 0; i < booking.pssList.length; i++) {
    const pss = booking.pssList[i];

    // restToPay after this accounting is created will be (restToPay - ae values we're currently creating)
    const futureRestToPay = restToPay.value - newlyAddedAccountingEntriesValue;

    if (
      isPSSPaid(pss) ||
      pss.bookingStatus === BookingStatus.Canceled ||
      pss.bookingStatus === BookingStatus.Abandoned ||
      futureRestToPay <= 0
    ) {
      continue;
    }

    let pssUnpaidVal = 0;
    pssUnpaidVal = pssUnpaidValue(pss);

    // start by rounding down the accounting entry value
    const accountingEntryValue = Math.floor(
      Math.min(pssUnpaidVal, remainingAmount, futureRestToPay)
    );

    newlyAddedAccountingEntriesValue += accountingEntryValue;

    // payment currency value rounded to the nearest integer
    let paymentCurrencyValue;
    if (accountingEntryValue === remainingAmount) {
      paymentCurrencyValue = Math.round(amountProvided * exchangeRate);
    } else {
      paymentCurrencyValue = Math.round(accountingEntryValue * exchangeRate);
    }

    // create AE only if the value is positive
    if (accountingEntryValue > 0) {
      const uuid = generateID();
      const creationTime = dayjs().toISOString();
      const accountingEntry: IAccountingEntry = {
        _id: uuid,
        uuid: uuid,
        accountingUuid: accountingUuid,
        orderSessionId: String(BookingModule.currentOrderSessionId),
        productSlotStateUuid: pss.uuid,
        value: accountingEntryValue,
        paymentCurrencyValue: paymentCurrencyValue,

        groupId: pss.productOwnerGroupId,
        bookingStatementVersion: 2,
        createdAt: creationTime,
        updatedAt: creationTime,
        productId: pss.productId,
      };
      if (pss.id) {
        accountingEntry.productSlotStateId = pss.id;
      }
      accEntries.push(accountingEntry);
      remainingAmount = remainingAmount - accountingEntryValue;
      amountProvided =
        amountProvided - Math.round(paymentCurrencyValue * reverseExchangeRate);
    }
  }

  // Distribute any rounding compensation backwards through entries in increments of 1 cent
  if (accEntries.length > 0) {
    const totalValue = accEntries.reduce((sum, entry) => sum + entry.value, 0);
    const targetValue = Math.min(restToPay.value, amount);

    let compensationAmount = Math.ceil(targetValue - totalValue);

    if (compensationAmount > 0) {
      LogService.info(
        `Distributing compensation amount ${compensationAmount} across ${accEntries.length} entries`
      );

      // Walk backwards through entries, adding 1 cent at a time
      let entryIndex = accEntries.length - 1;
      while (compensationAmount > 0 && entryIndex >= 0) {
        accEntries[entryIndex].value += 1;
        compensationAmount -= 1;

        entryIndex--;
        // Loop back to the last entry if we haven't distributed all compensation
        if (entryIndex < 0 && compensationAmount > 0) {
          entryIndex = accEntries.length - 1;
        }
      }
    }
  }

  // Belgium: cash totals settle to the nearest 5 cents. Only the payment that
  // closes the booking is rounded — rounding a partial split payment would
  // shift the difference onto the next one.
  let roundingDifference = 0;
  if (isCash && POSConfigModule.requiresCashRounding && accEntries.length > 0) {
    const accEntriesTotal = sumArray(accEntries.map((entry) => entry.value));
    const settlesBooking = accEntriesTotal >= restToPay.value;
    if (settlesBooking && !isMultipleOf5(accEntriesTotal)) {
      const desiredValue = roundToNearestMultipleOf5(accEntriesTotal);
      roundingDifference = accEntriesTotal - desiredValue;
      accEntries[accEntries.length - 1].value += desiredValue - accEntriesTotal;
      LogService.info(
        `Belgian cash rounding applied: ${accEntriesTotal} -> ${desiredValue}`,
        { roundingDifference, booking: booking.uuid }
      );
    }
  }

  return { accEntries, roundingDifference };
}

/**
 *
 * @param booking
 * @returns a promocodeDiscount accounting for the selected booking
 */
export function createPromocodeDiscountAccounting(
  booking: IProductSlotBooking,
  promocode: IPromocode
): IAccounting {
  // const promocode = BookingModule.appliedPromocode?.promocode;
  const uuid = generateID();
  const creationTime = dayjs().toISOString();
  const accountingState = AccountingState.Pending;
  let transactionType = 2;
  if (
    promocode.promoCodeType === PromocodeCampaignType.TYPE_VOUCHER ||
    promocode.promoCodeType === PromocodeCampaignType.TYPE_VOUCHER_SOLD ||
    promocode.promoCodeType === PromocodeCampaignType.TYPE_GIFT_CARD ||
    promocode.promoCodeType === PromocodeCampaignType.TYPE_GIFT_CARD_SOLD
  ) {
    transactionType = TransactionTypes.TRANSACTION_TYPE_PAYMENT;
  } else if (
    promocode.promoCodeType === PromocodeCampaignType.TYPE_MEMBERSHIP_SOLD ||
    promocode.promoCodeType === PromocodeCampaignType.TYPE_MEMBERSHIP ||
    promocode.promoCodeType === PromocodeCampaignType.TYPE_PROMO_CODE
  ) {
    transactionType = TransactionTypes.TRANSACTION_TYPE_DISCOUNT;
  }

  let paymentMethod = "";
  switch (promocode.promoCodeType) {
    case PromocodeCampaignType.TYPE_VOUCHER:
    case PromocodeCampaignType.TYPE_VOUCHER_SOLD:
      paymentMethod = PaymentMethods.POS_VOUCHER;
      break;
    case PromocodeCampaignType.TYPE_GIFT_CARD:
    case PromocodeCampaignType.TYPE_GIFT_CARD_SOLD:
      paymentMethod = PaymentMethods.POS_GIFT_CARD;
      break;
    case PromocodeCampaignType.TYPE_PROMO_CODE:
      paymentMethod = PaymentMethods.POS_PROMO_CODE;
      break;
    case PromocodeCampaignType.TYPE_MEMBERSHIP:
    case PromocodeCampaignType.TYPE_MEMBERSHIP_SOLD:
      paymentMethod = PaymentMethods.POS_MEMBERSHIP;
      break;
  }

  // INVALID PROMOCODE FOR TESTING SYNCH PENDING BOOKING WITH INVALID PROMOCODE
  const invalidPromocodeCode = "55555";
  const invalidPromocodeId = 6609;

  // link the transaction to the new accounting
  const transaction =
    FiskalyModule.transactionsInProgress[String(booking.uuid)];

  const accounting: IAccounting = {
    _id: uuid,
    uuid: uuid,
    orderSessionId: String(BookingModule.currentOrderSessionId),
    state: accountingState,
    transactionType: transactionType,
    paymentMethod: paymentMethod as PaymentMethods,
    bookingUuid: booking.uuid,
    promoCodeId: promocode.promoCodeId /* invalidPromocodeId*/,
    promoCodeUuid: promocode.code /* invalidPromocodeCode*/,
    createdAt: creationTime,
    updatedAt: creationTime,
    message: creationTime + " :: accounting was created.",
    isDeposit: false,
    currency: booking.currency,
    accountingEntriesList: [],
    cashRegisterId: DeviceModule.cashRegisterId,
    channel: "Smeetz POS",
    merchantReference: "",
    CfUid: CashFloatModule.cashFloat?.uid,
    createdBy: AuthModule.cognitoId,
    promocode: promocode.code,
    fiskalyTransactionUuid: transaction ? transaction.uuid : null,
  };

  // Lets now create accounting entries for pss with promocodeDiscount
  return accounting;
}

/**
 *
 * @returns Creates an accounting of type Free for the booking
 * without accounting entries, value = 0, payment_method = “FREE”,
 * or null when the booking already carries one
 */
export function createFreeAccounting(
  booking: IProductSlotBooking
): IAccounting | null {
  const hasFreeAccounting = booking.accountings.some(
    (acc) => acc.paymentMethod === PaymentMethods.METHODS_POS_FREE
  );
  if (hasFreeAccounting) {
    // A booking is only free once; a second FREE accounting would double-count.
    LogService.info(
      `Booking ${booking.uuid} already carries a FREE accounting, so no second one is created`
    );
    return null;
  }
  const uuid = generateID();
  const creationTime = dayjs().toISOString();
  const accountingState = AccountingState.Pending;

  const transactionType = TransactionTypes.TRANSACTION_TYPE_PAYMENT;

  const accounting: IAccounting = {
    _id: uuid,
    uuid: uuid,
    orderSessionId: String(BookingModule.currentOrderSessionId),
    state: accountingState,
    transactionType: transactionType,
    paymentMethod: PaymentMethods.METHODS_POS_FREE,
    bookingUuid: booking.uuid,
    promoCodeUuid: null,
    createdAt: creationTime,
    updatedAt: creationTime,
    message: creationTime + " :: accounting was created.",
    isDeposit: false,
    currency: booking.currency,
    accountingEntriesList: [],
    cashRegisterId: DeviceModule.cashRegisterId,
    channel: "Smeetz POS",
    merchantReference: "",
    CfUid: CashFloatModule.cashFloat?.uid,
    createdBy: AuthModule.cognitoId,
  };

  booking.accountings.push(accounting);
  LogService.info(
    `Free accounting created, booking: ${booking.uuid}, accounting: ${accounting.uuid}`,
    {
      accounting: accounting,
    }
  );
  return accounting;
}

/**
 * Called to confirm an entered Cash amount OR when user complete a CARD payment
 * It creates accounting. And in case of CASH, it confirms the amount to be displayed in the order summary
 */
export async function applyPayment(bookingCopy?: IProductSlotBooking) {
  const selectedBooking = bookingCopy
    ? bookingCopy
    : BookingModule.selectedBooking;
  if (!selectedBooking) {
    throw new Error(PSBErrors.NoBookingInModule);
  }
  // let accountingInfo: IAccountingInfo = { paymentTool: paymentTool.value };

  // =======
  const uuid = selectedBooking.uuid as string;
  if (!uuid) {
    throw new Error(PSBErrors.HasNoUUID);
  }

  const bookingPayment = BookingModule.selectedBookingPayment;
  if (!bookingPayment || !bookingPayment.paymentToolId) {
    LogService.info(`Booking has no payment chosen, booking: ${uuid}`, {
      bookingPayment,
    });
    throw new Error("Booking has no payment chosen");
  }

  // Remove all other cash accountings and confirmed amounts.
  // Because only one cash payment is allowed per booking.
  // TODO Hamza: If EditOrder, then we keep the completed CASH accountings
  if (bookingPayment.paymentToolId === PaymentMethodTypes.Cash) {
    let withoutCashAccountings: IAccounting[] = [];
    const isEditOrder = await isBookingInDexie(uuid);
    if (isEditOrder || BookingModule.doesSupportSplitPayment) {
      // isEditOrder true, going to delete all CASH accountings except completed ones
      withoutCashAccountings = selectedBooking.accountings.filter(
        (acc) =>
          acc.paymentMethod !== PaymentMethods.METHOD_POS_CASH ||
          (acc.paymentMethod === PaymentMethods.METHOD_POS_CASH &&
            acc.state === AccountingState.Completed)
      );
    } else {
      // isEditOrder false, going to delete all CASH accountings
      withoutCashAccountings = selectedBooking.accountings.filter(
        (acc) => acc.paymentMethod !== PaymentMethods.METHOD_POS_CASH
      );
    }

    BookingModule.updateBookingAccountings({
      bookingUUID: uuid,
      accountings: withoutCashAccountings,
    });

    // Since only one cash amount could be applied, remove pre-existing cash amounts
    const confirmedAmounts =
      BookingModule.bookingsAmountsTrack[uuid].confirmedAmounts;
    const withoutCashConfirmedAmounts = confirmedAmounts.filter(
      (amount) =>
        amount.accountingInfo.paymentToolId !== PaymentMethodTypes.Cash
    );
    BookingModule.updateConfirmedAmounts({
      bookingUUID: uuid,
      confirmedAmounts: withoutCashConfirmedAmounts,
    });
  }

  // remove any previous promocode accounting, except completed ones
  if (BookingModule.isPromocodeApplied) {
    let keptAccounting: IAccounting[] = [];
    // going to delete all accountings except completed ones
    // we need to keep completed accountings even if they are of Promocode
    keptAccounting = selectedBooking.accountings.filter(
      (acc) =>
        (acc.paymentMethod !== PaymentMethods.POS_PROMO_CODE &&
          acc.paymentMethod !== PaymentMethods.POS_VOUCHER &&
          acc.paymentMethod !== PaymentMethods.POS_GIFT_CARD &&
          acc.paymentMethod !== PaymentMethods.POS_MEMBERSHIP) ||
        ((acc.paymentMethod === PaymentMethods.POS_PROMO_CODE ||
          acc.paymentMethod === PaymentMethods.POS_VOUCHER ||
          acc.paymentMethod === PaymentMethods.POS_GIFT_CARD ||
          acc.paymentMethod === PaymentMethods.POS_MEMBERSHIP) &&
          (acc.state === AccountingState.Completed ||
            acc.state === AccountingState.Pending))
    );
    BookingModule.updateBookingAccountings({
      bookingUUID: uuid,
      accountings: keptAccounting,
    });
  }

  // convert paymentToolId:PaymentMethodTypes to PaymentMethods
  const paymentTool =
    PaymentMethodTypeToPaymentTool[
      bookingPayment.paymentToolId as PaymentMethodTypes
    ];
  const paymentMethod = PaymentToolMethod[paymentTool];

  const createdAccounting = await createPosAccounting(
    selectedBooking,
    // PaymentToolMethod[accountingInfo.paymentTool], //PaymentMethods.METHOD_POS_ADYEN,
    paymentMethod,
    // accountingInfo,
    bookingPayment
  );

  const accountingInfo: IAccountingInfo = {
    // paymentTool: BookingModule.bookingsAmountsTrack[uuid].paymentTool,
    paymentTool: bookingPayment.paymentTool,
    accountingUUId: createdAccounting.uuid,
    paymentToolId: bookingPayment.paymentToolId,
  };

  // await createPaymentData(selectedBooking, accountingInfo);
  BookingModule.updateCurrentAmount({
    BookingUUID: BookingModule.selectedBookingUUID,
    // amount: BookingModule.pssSumFinaValues / 100,
    amount: BookingModule.currentAmount
      ? BookingModule.currentAmount
      : restToPay.value /*BookingModule.pssSumFinaValues*/ / 100,
  });
  BookingModule.confirmCurrentAmount({
    BookingUUID: BookingModule.selectedBookingUUID,
    accountingInfo,
  });
  BookingModule.updateBooking(selectedBooking);
  return;
}

/**
 * Remove accountings of a given booking. It keeps completed accountings
 * @param id
 * @returns
 */
export function deleteUncompletedBookingAccountings(
  bookingCopy: IProductSlotBooking | null
): IProductSlotBooking {
  if (!bookingCopy) {
    throw new Error(PSBErrors.NoBookingInModule);
  }
  LogService.info(
    `Processing booking accountings removal, booking: ${bookingCopy?.uuid}`
  );

  const boookingUUID = bookingCopy.uuid;
  if (!boookingUUID) {
    throw new Error(PSBErrors.HasNoUUID);
  }

  const keptAccountings: IAccounting[] = [];
  const completedAccountings = bookingCopy.accountings.filter(
    (acc) => acc.state === AccountingState.Completed
  );
  completedAccountings.forEach((acc) => keptAccountings.push(acc));
  const discountAccountings = bookingCopy.accountings.filter(
    (acc) =>
      acc.state === AccountingState.Pending &&
      acc.transactionType === TransactionTypes.TRANSACTION_TYPE_DISCOUNT
  );
  discountAccountings.forEach((acc) => keptAccountings.push(acc));
  const paymentCodeAccountings = bookingCopy.accountings.filter(
    (acc) =>
      acc.state === AccountingState.Pending &&
      (acc.promoCodeUuid || acc.promoCodeId || acc.promocode) &&
      acc.transactionType === TransactionTypes.TRANSACTION_TYPE_PAYMENT
  );
  paymentCodeAccountings.forEach((acc) => keptAccountings.push(acc));
  bookingCopy.accountings = [];
  bookingCopy.accountings = keptAccountings;
  // updatedBooking.accountings.concat(keptAccountings);
  // BookingModule.updateBooking(bookingCopy);
  // }
  // THERE IS NO ELSE ANYMORE BECAUSE ALL BOOKINGS SUPPORT SPLIT PAYMENT
  // else {
  //   // going to delete all accountings
  //   updatedBooking.accountings = [];
  // }
  // BookingModule.updateBooking(updatedBooking);
  LogService.info(
    `Done removing unnecessary booking accountings, booking: ${bookingCopy?.uuid}`
  );
  return bookingCopy;
}

/**
 * Remove accountings of a given booking
 * @param id
 * @returns
 */
export function deleteBookingAmountsTrack(booking: IProductSlotBooking | null) {
  if (!booking) {
    throw new Error(PSBErrors.NoBookingInModule);
  }

  const boookingUUID = booking.uuid;
  if (!boookingUUID) {
    throw new Error(PSBErrors.HasNoUUID);
  }

  // 1- empty confirmedAmounts of the booking
  BookingModule.removeAmountTracking(boookingUUID);
}

/**
 * Initiates amountTracking object for the booking
 */
export function initiateAmountsTracking(
  BookingUUID: string,
  track: IBookingPayment
) {
  BookingModule.addPaymentTracking({ BookingUUID, track });
}

/**
 * @param accountings T
 * @returns payments done in the booking
 */
export function getReceiptPayments(
  accountings: IAccounting[]
): IReceiptPayment[] {
  const payments: IReceiptPayment[] = [];
  // Belgian cash rounding is reported as a single aggregated line at the end
  // rather than per accounting, so it is collected here and skipped below.
  const roundingValues: number[] = [];
  let roundingCurrency = "";

  // 1- Handle payments accountings
  for (const ac of accountings) {
    if (ac.paymentMethod === PaymentMethods.METHOD_CASH_ROUNDING) {
      roundingValues.push(
        ...ac.accountingEntriesList.map((entry) => entry.value)
      );
      roundingCurrency = ac.paymentCurrency || ac.currency || roundingCurrency;
      continue;
    }
    if (
      ac.paymentMethod === PaymentMethods.METHOD_Swiss_QR_Code_Bank_Transfer &&
      ac.state !== AccountingState.Completed
    ) {
      continue;
    }
    if (
      ac.transactionType === TransactionTypes.TRANSACTION_TYPE_TAX ||
      ac.transactionType === TransactionTypes.TRANSACTION_TYPE_DISCOUNT ||
      ac.transactionType === TransactionTypes.TRANSACTION_TYPE_TICKET ||
      ac.transactionType === TransactionTypes.TRANSACTION_TYPE_REFUND ||
      ac.paymentMethod === PaymentMethods.METHODS_POS_FREE ||
      ac.state === AccountingState.Failed
    ) {
      continue;
    }
    if (
      ac.paymentMethod === PaymentMethods.METHOD_POS_CASH &&
      ac.currency !== ac.paymentCurrency &&
      ac.providedCashBill
    ) {
      /*
       if payment was in secondary currency we print the provided
       cash bill in that currency and change given back in main currency
      */
      let value = ac.providedCashBill * (1 / (ac.exchangeRate || 1));
      if (ac.currency === Currencies.CHF) {
        value = roundToNearestMultipleOf5(Math.round(value));
      }
      payments.push({
        name: t(`payment-method.${ac.paymentMethod}`),
        providedCashBill: (ac.providedCashBill / 100).toFixed(2),
        exchangeRate: (ac.exchangeRate || 1).toFixed(2),
        value: (value / 100).toFixed(2),
        currency: ac.paymentCurrency || ac.currency || "CHF",
      });
      if (ac.paymentAndChange) {
        // consider change given back a payment row on the receipt
        const changeValue = ac.paymentAndChange.changeValue;
        payments.push({
          name: t(`multicurrencies.change`),
          value: ((changeValue * -1) / 100).toFixed(2),
          currency: ac.currency || "CHF",
        });
      }
    } else if (
      ac.paymentMethod === PaymentMethods.METHOD_POS_CASH &&
      ac.currency === ac.paymentCurrency &&
      ac.providedCashBill
    ) {
      // if paid in cash with main currency then
      // print the provided cash bill of the main currency
      // and the change given back (if any) by substracting the accounting entry values
      let aeValueSum = 0;
      ac.accountingEntriesList.forEach((ae) => {
        aeValueSum += ae.value * (ac.exchangeRate || 1);
      });
      payments.push({
        name: t(`payment-method.${ac.paymentMethod}`),
        value: (ac.providedCashBill / 100).toFixed(2),
        currency: ac.paymentCurrency || ac.currency || "CHF",
      });
      const changeValue = ac.providedCashBill - aeValueSum;
      if (changeValue > 0) {
        // only print if there was a change
        payments.push({
          name: t(`multicurrencies.change`),
          value: ((changeValue * -1) / 100).toFixed(2),
          currency: ac.currency || "CHF",
        });
      }
    } else {
      // otherwise print the payment method with value of accounting entries
      let value = 0;
      ac.accountingEntriesList.forEach((ae) => {
        value += ae.value * (ac.exchangeRate || 1);
      });
      payments.push({
        name:
          ac.paymentMethod === PaymentMethods.METHOD_Swiss_QR_Code_Bank_Transfer
            ? t(`payment-method.BANK_TRANSFER_SWISS_QR`)
            : ac.paymentMethod !== PaymentMethods.EXTERNAL_INCERT_VOUCHER
            ? t(`payment-method.${ac.paymentMethod}`)
            : String(translatePaymentMethods(String(ac.paymentMethod))) +
              " " +
              ac.promocode,
        value: (value / 100).toFixed(2),
        currency: ac.paymentCurrency || ac.currency || "CHF",
      });
    }
  }
  // 2- Now handle refund acountings
  const refunds = accountings.filter(
    (acc) => acc.transactionType === TransactionTypes.TRANSACTION_TYPE_REFUND
  );
  const groupedRefundsAccountings: {
    [method: string]: IAccounting[];
  } = {};
  // Lets group refund accountings based on their paymentMethods
  for (const ac of refunds) {
    if (ac.paymentMethod === PaymentMethods.METHOD_CASH_ROUNDING) {
      // already collected above, into the single aggregated rounding line
      continue;
    }
    if (groupedRefundsAccountings[ac.paymentMethod]) {
      // This payment method is already grouped lets push this ac
      groupedRefundsAccountings[ac.paymentMethod].push(ac);
    } else {
      groupedRefundsAccountings[ac.paymentMethod] = [];
      groupedRefundsAccountings[ac.paymentMethod].push(ac);
    }
  }
  // Now foreach grouped accountings we will calculate the sum of their accounting entries
  for (const paymentMethod in groupedRefundsAccountings) {
    let value = 0;
    groupedRefundsAccountings[paymentMethod]
      .map((ac) => ac.accountingEntriesList)
      .flat()
      .forEach((ae) => {
        value += ae.value;
      });
    payments.push({
      name: t(`payment-method.${paymentMethod}`),
      value: (value / 100).toFixed(2),
      currency: groupedRefundsAccountings[paymentMethod][0].currency || "CHF",
    });
  }

  // 3- Belgian cash rounding, as one line. Negated so the receipt shows the
  // adjustment from the customer's point of view.
  const roundingTotal = sumArray(roundingValues);
  if (roundingTotal !== 0 && roundingCurrency) {
    payments.push({
      name: t(`payment-method.CASH_ROUNDING`),
      value: ((roundingTotal / 100) * -1).toFixed(2),
      currency: roundingCurrency,
    });
  }
  return payments;
}

/**
 * This functions creates and returns a list of Ticket Type accountings
 * It is called only when the payment is completed (in case of Edit/Cancel Order the order can be partially paid) and before processing synching the booking
 * It takes an original booking, and an edited copy of it as parameters
 * Before adding Edit/Cancel Order feature in this app, this function is only called after completing the payment and before synching the booking to backend
 */
export function createTicketAccountings(
  booking: IProductSlotBooking,
  originalBooking?: IProductSlotBooking | null | undefined
): IAccounting[] {
  LogService.info(
    `Processing creation of TICKET_POS type accountings for the booking: ${booking.uuid}`,
    {
      params: booking,
    }
  );
  const accountings: IAccounting[] = [];
  const existingTicketTypeAccounting = booking.accountings.filter(
    (acc) =>
      acc.state === AccountingState.Completed &&
      acc.transactionType === TransactionTypes.TRANSACTION_TYPE_TICKET
  );
  const existingTicketTypeAccEntries = existingTicketTypeAccounting
    .map((acc) => acc.accountingEntriesList)
    .flat();
  // For each pss that is not cancelled and that doesnt have TICKET type accounting yet we create one with full original price but negative value
  const pssList = booking.pssList;
  for (const pss of pssList) {
    if (
      pss.bookingStatus === BookingStatus.Canceled ||
      pss.bookingStatus === BookingStatus.Abandoned
    ) {
      // this pss is cancelled/abandoned, lets not create TICKET type accounting for it
      // cancel/refund type accounting will be created explicitly for it later
      continue;
    }
    const index = existingTicketTypeAccEntries.findIndex(
      (ae) => ae.productSlotStateUuid === pss.uuid
    );
    if (index >= 0) {
      // this pss has already a ticket type accounting for it
      continue;
    }
    accountings.push(createTicketAccounting(pss, booking.currency));
  }

  // const pssListBalances = BookingModule.pssListBalances;

  // const positiveBalances = pssListBalances.filter((b) => b.balance > 0);
  // for (const balance of positiveBalances) {
  //   accountings.push(createTicketAccounting(balance, booking.currency));
  // }
  LogService.info(
    `Done creating TICKET_POS type accountings for the booking: ${booking.uuid}`,
    {
      params: accountings,
    }
  );
  return accountings;
}

/**
 * returns an accounting of type ticket related to one pss with negative value
 * @param pss of type IPSS
 */
export function createTicketAccounting(
  pss: IProductSlotState,
  currency: Currencies
): IAccounting {
  const creationTime = dayjs().toISOString();
  const accUuid = generateID();

  const pssValue = getPSSValue(pss);
  const accountingEntryValue = pssValue * -1;

  // link the transaction to the new accounting
  const transaction =
    FiskalyModule.transactionsInProgress[String(pss.bookingUuid)];

  const accountingEntry: IAccountingEntry = {
    _id: generateID(),
    uuid: generateID(),
    accountingUuid: accUuid,
    orderSessionId: String(BookingModule.currentOrderSessionId),
    productSlotStateUuid: pss.uuid,
    value: accountingEntryValue,
    groupId: pss.productOwnerGroupId,
    bookingStatementVersion: 2, // What is this?
    createdAt: creationTime,
    updatedAt: creationTime,
    productId: pss.productId,
  };

  const priceTaxes = getpssExtension(String(pss.uuid))?.taxes || [];
  if (priceTaxes.length) {
    const tax = priceTaxes[0];
    accountingEntry.salesTaxMicroValue = -1 * getTaxValue(pssValue, tax);
    accountingEntry.salesTaxRate = Number(tax.rateMiliValue);
    accountingEntry.salesTaxIncluded = Number(tax.includedInPrice);
    accountingEntry.taxId = Number(tax.id);
  }

  const accounting: IAccounting = {
    _id: accUuid,
    uuid: accUuid,
    orderSessionId: String(BookingModule.currentOrderSessionId),
    createdAt: creationTime,
    updatedAt: creationTime,
    state: AccountingState.Completed,
    transactionType: TransactionTypes.TRANSACTION_TYPE_TICKET,
    promoCodeUuid: null,
    paymentMethod: PaymentMethods.METHODS_TICKET_POS,
    message: creationTime + " :: accounting was created.",
    isDeposit: false,
    currency: currency,
    bookingUuid: pss.bookingUuid,
    cashRegisterId: DeviceModule.cashRegisterId,
    channel: "Smeetz POS",
    merchantReference: "",
    CfUid: CashFloatModule.cashFloat?.uid,
    createdBy: AuthModule.cognitoId,
    accountingEntriesList: [accountingEntry],
    fiskalyTransactionUuid: transaction ? transaction.uuid : null,
  };
  if (pss.id) {
    accounting.accountingEntriesList[0].productSlotStateId = pss.id;
  }

  return accounting;
}

/**
 * returns an accounting of type ticket related to one pss that is cancelled, with positive value and refund:1
 */
export function createCancelPSSAccounting(
  pss: IProductSlotState,
  currency: Currencies
): IAccounting {
  LogService.info(
    `Going to create cancellation accounting for attached pss ${pss.uuid}`,
    {
      params: pss,
    }
  );
  const cancellationAccounting = createTicketAccounting(pss, currency);
  // This is a paid pss cancellation, accEntry value should be positive
  cancellationAccounting.accountingEntriesList[0].value = Math.abs(
    cancellationAccounting.accountingEntriesList[0].value
  );
  cancellationAccounting.refund = 1;

  if (cancellationAccounting.accountingEntriesList[0].salesTaxMicroValue) {
    cancellationAccounting.accountingEntriesList[0].salesTaxMicroValue =
      Math.abs(
        cancellationAccounting.accountingEntriesList[0].salesTaxMicroValue
      );
  }
  LogService.info(
    `Done creating cancellation accounting for the pss ${pss.uuid}`,
    {
      params: cancellationAccounting,
    }
  );
  return cancellationAccounting;
}

/**
 * returns an accounting/list of accountings of type ticket related to one pss that is cancelled so that we refund it, with negative value and refund:1
 */
export function createRefundPSSAccounting(
  amount: number,
  paymentMethod: PaymentMethods
): IAccounting[] {
  console.log("------------ hi from createRefundPSSAccounting ------------");
  console.log("amount", amount);
  console.log("paymentMethod", paymentMethod);
  /** TODO @Aymen: Please improve the below logic with more details and move into a confluence doc and share it
   * 1pss:
   * (old logic before taxes)
   * 1 payment acc with positive value, created when we paid
   * 1 ticket acc with negative value, created when we paid
   *  balance here is 0.
   *
   * (new logic with taxes)
   * 1 payment acc with positive value, created when we paid
   * 1 ticket acc with negative value, created when we paid
   * 1 tax acc with negative value, created when we paid
   *
   * We cancel a ticket (before taxes):
   * 1 ticket acc with refund:1 and positive value, created when user confirmed the cancellation
   * We need to create 1 acc of refund type with refund: 1 and negative value
   * balance: 0
   *
   *
   * We cancel a ticket (after taxes logic):
   * 1 ticket acc with refund:1 and positive value, created when user confirmed the cancellation
   * 1 tax acc with refund:1 and positive value, created when user confirmed the cancellation
   *
   * Refund moment:
   * We need to create 1 acc of refund type with refund: 1 and negative value
   * if tax is included, we should do nothing
   * if tax is excluded, we need to express that we need to refund also the tax value that was paid:
   * so we update refundAccounting.accountingEntry value to not only include pss.priceValue, but pss.pricevalue + pss.taxValue
   * balance: 0
   *
   */

  // get pss that needs to be refunded, they are the cancelled one that have positive balance
  const booking = BookingModule.selectedBooking;
  if (!booking) {
    throw new Error(PSBErrors.NoBookingInModule);
  }
  const bookingCopy = cloneDeep(booking);

  const cancelledOrAbandonedPss = bookingCopy.pssList.filter(
    (pss) =>
      pss.bookingStatus === BookingStatus.Canceled ||
      pss.bookingStatus === BookingStatus.Abandoned
  );
  if (!cancelledOrAbandonedPss || !cancelledOrAbandonedPss.length) {
    return [];
  }
  // Find the ones we need to refund, they are the one that have positive balance
  const cancelledPssUuidString = cancelledOrAbandonedPss
    .map((pss) => pss.uuid)
    .join(" - ");
  const pssBalances = BookingModule.pssListBalances.filter(
    (pss) =>
      cancelledPssUuidString.includes(String(pss.pssUuid)) && pss.balance > 0
  );
  LogService.info(
    `Going to create refund accountings for all pss to refund attached`,
    {
      params: pssBalances.map((p) => p.pss),
    }
  );
  // The operator hands the money back once, so this is ONE refund accounting
  // carrying an entry per refunded ticket — not one accounting per ticket.
  const creationTime = dayjs().toISOString();
  const accUuid = generateID();
  // link the transaction to the new accounting
  const transaction =
    FiskalyModule.transactionsInProgress[String(booking.uuid)];
  const refundAccounting: IAccounting = {
    _id: accUuid,
    uuid: accUuid,
    orderSessionId: String(BookingModule.currentOrderSessionId),
    CfUid: CashFloatModule.cashFloat?.uid,
    refund: 1,
    createdAt: creationTime,
    updatedAt: creationTime,
    state: AccountingState.Completed,
    transactionType: TransactionTypes.TRANSACTION_TYPE_REFUND,
    promoCodeUuid: null,
    paymentMethod: paymentMethod,
    message: creationTime + " :: accounting was created.",
    isDeposit: false,
    currency: BookingModule.selectedBooking?.currency,
    bookingUuid: booking.uuid,
    cashRegisterId: DeviceModule.cashRegisterId,
    channel: "Smeetz POS",
    merchantReference: "",
    createdBy: AuthModule.cognitoId,
    fiskalyTransactionUuid: transaction ? transaction.uuid : null,
    accountingEntriesList: [],
    amountReturned: 0,
  };

  // the amount for each entry is the minimum of pss.balance and the amount
  // supplied, multiplied by minus one
  for (let i = 0; i < pssBalances.length; i++) {
    const pssToRefund = pssBalances[i].pss;
    const balance = pssBalances[i].balance;
    if (Math.abs(balance) === 1) {
      // rounding issue, skip this pss
      continue;
    }
    const paymentsOfPss = paymentByPss.value(String(pssToRefund.uuid));
    const discountsOfPss = totalDiscountsByPss.value(String(pssToRefund.uuid));
    const refundsOfPss = refundsByPss.value(String(pssToRefund.uuid));
    const pssFinalValue = Math.round(
      paymentsOfPss + discountsOfPss + refundsOfPss
    );

    const accountingEntry: IAccountingEntry = {
      _id: generateID(),
      uuid: generateID(),
      accountingUuid: accUuid,
      orderSessionId: String(BookingModule.currentOrderSessionId),
      productSlotStateUuid: pssToRefund.uuid,
      value: Math.max(-1 * pssFinalValue, amount),
      groupId: pssToRefund.productOwnerGroupId,
      bookingStatementVersion: 2,
      createdAt: creationTime,
      updatedAt: creationTime,
      productId: pssToRefund.productId,
    };
    if (pssToRefund.id) {
      accountingEntry.productSlotStateId = pssToRefund.id;
    }
    refundAccounting.accountingEntriesList.push(accountingEntry);
    amount = amount - accountingEntry.value;
  }

  const refundAccountings: IAccounting[] = refundAccounting
    .accountingEntriesList.length
    ? [refundAccounting]
    : [];

  const lastEntry = () =>
    refundAccounting.accountingEntriesList[
      refundAccounting.accountingEntriesList.length - 1
    ];

  if (amount && refundAccountings.length) {
    // we have still some amount to refund but no pss to attach it to
    // we attach it to the last refund entry
    lastEntry().value += amount;
  }

  // Belgium: cash refunds are handed back in multiples of 5 cents, so the
  // difference is booked as its own refund line.
  if (
    refundAccountings.length &&
    paymentMethod === PaymentMethods.METHOD_POS_CASH &&
    POSConfigModule.requiresCashRounding
  ) {
    const refundedTotal = sumArray(
      refundAccounting.accountingEntriesList.map((entry) => entry.value)
    );
    if (!isMultipleOf5(refundedTotal)) {
      // refund totals are negative, and roundToNearestMultipleOf5 is not
      // sign-symmetric, so round the magnitude and flip back
      const desiredValue = roundToNearestMultipleOf5(refundedTotal * -1) * -1;
      const roundingDifference = refundedTotal - desiredValue;
      lastEntry().value += desiredValue - refundedTotal;

      const roundingAccounting = createCashRoundingRefundAccounting(
        booking,
        roundingDifference,
        pssBalances.map((pssBalance) => pssBalance.pss)
      );
      if (roundingAccounting) {
        refundAccountings.push(roundingAccounting);
      }
      LogService.info(
        `Belgian cash rounding applied to refund: ${refundedTotal} -> ${desiredValue}`,
        { roundingDifference, booking: booking.uuid }
      );
    }
  }

  // set once, after every adjustment to the entries above
  refundAccounting.amountReturned =
    sumArray(refundAccounting.accountingEntriesList.map((e) => e.value)) * -1;

  LogService.info(
    `Done creating refund accounting for pss list ${pssBalances
      .map((p) => p.pss.uuid)
      .join(" - ")}`,
    {
      params: refundAccountings,
    }
  );
  return refundAccountings;
}

/**
 * this function cteate and returns a new accounting object of type TAX without accountingEntries
 * @param booking of type IPSB
 */
export function initTaxAccounting(
  booking: IProductSlotBooking,
  currency: Currencies
): IAccounting {
  const creationTime = dayjs().toISOString();
  const accUuid = generateID();
  const accounting: IAccounting = {
    _id: accUuid,
    uuid: accUuid,
    orderSessionId: String(BookingModule.currentOrderSessionId),
    createdAt: creationTime,
    updatedAt: creationTime,
    state: AccountingState.Completed,
    transactionType: TransactionTypes.TRANSACTION_TYPE_TAX,
    promoCodeUuid: null,
    paymentMethod: PaymentMethods.METHODS_TAX_POS,
    message: creationTime + " :: accounting was created.",
    isDeposit: false,
    currency: currency,
    bookingUuid: booking.uuid,
    cashRegisterId: DeviceModule.cashRegisterId,
    channel: "Smeetz POS",
    CfUid: CashFloatModule.cashFloat?.uid,
    accountingEntriesList: [],
    merchantReference: "",
    createdBy: AuthModule.cognitoId,
  };

  return accounting;
}

/**
 * This functions creates and returns TAX accountingEntries for one pss if the pss has Taxes else it returns []
 * @param pss of type IPSS
 * @param accounting of type IAccounting
 */
export function createTaxAccountingEntries(
  pss: IProductSlotState,
  taxAccounting: IAccounting
): IAccountingEntry[] {
  const taxTypeAccEntries = [];
  const pssListWithTaxes: {
    pssUuid: string;
    pssValue: number;
    taxes: ITax[];
  }[] = [];
  // group all PSS with their taxes in pssListWithTaxes.
  // const bookingLifeTimePssValues = BookingModule.bookingLifeTimePssValues;
  const booking = cloneDeep(BookingModule.selectedBooking);
  const pssList = booking?.pssList;
  if (!pssList || !pssList.length) {
    return [];
  }
  for (const pss of pssList) {
    // pss final value after discounts
    let pssValue = getPSSValue(pss);
    pssValue -= totalDiscountsByPss.value(String(pss.uuid));
    if (!pssValue) continue;
    let taxes: ITax[] = [];
    const pssExt = getpssExtension(String(pss.uuid));
    if (pssExt && pssExt.taxes) {
      taxes = pssExt.taxes;
    }
    if (!taxes || !taxes.length) {
      continue;
    }
    pssListWithTaxes.push({
      pssUuid: String(pss.uuid),
      pssValue: pssValue,
      taxes: taxes,
    });
  }
  if (!pssListWithTaxes.length) return [];

  const pssWithTaxes = pssListWithTaxes.find((p) => p.pssUuid === pss.uuid);
  if (!pssWithTaxes || !pssWithTaxes.taxes.length) return [];
  // for each tax of this pss we create an AE with the tax value (negative) and countInBalance = 1 if the tax type is EXCl
  for (const tax of pssWithTaxes.taxes) {
    taxTypeAccEntries.push({
      _id: generateID(),
      uuid: generateID(),
      accountingId: taxAccounting.id || null,
      accountingUuid: taxAccounting.uuid,
      productSlotStateUuid: pss.uuid,
      value: -1 * getTaxValue(pssWithTaxes.pssValue, tax),
      groupId: pss.productOwnerGroupId,
      bookingStatementVersion: 2, // What is this?
      createdAt: dayjs().toISOString(),
      updatedAt: dayjs().toISOString(),
      productId: pss.productId,

      taxId: tax.id,
      vatRate: tax.rate,
      countInBalance: tax.includedInPrice === 0 ? 1 : 0,
    } as IAccountingEntry);
  }
  return taxTypeAccEntries;
}

/**
 * TODO Hamza: below function is changing the booking in place, it is costing a lot when the booking has many pss
 * This function is called after we do a payment
 * This functions updates the Tax accounting of the booking
 * Now for the pss objects that have payments and have TICKET accountings created above
 * we need to consider their tax value with new accountingEntries (negative value) to be attached to an existing TAX accounting
 * and if we don't have an existing TAX acc we just created
 */
export function updateTaxAccounting() {
  const booking1 = BookingModule.selectedBooking;
  if (!booking1) {
    throw new Error(PSBErrors.NoBookingInModule);
  }
  const bookingCopy = cloneDeep(booking1);
  const boookingUUID = bookingCopy.uuid;
  if (!boookingUUID) {
    throw new Error(PSBErrors.HasNoUUID);
  }

  // if the booking already has Tax accounting (not refund) we use it else we create one
  let taxTypeAccounting = bookingCopy.accountings.find(
    (acc) =>
      acc.state === AccountingState.Completed &&
      acc.transactionType === TransactionTypes.TRANSACTION_TYPE_TAX &&
      acc.refund !== 1 /** tax accounting was created when we  cancelled */
  );
  if (!taxTypeAccounting) {
    // So the booking didn't have a tax accounting after performing a payment, lets create one without ae
    taxTypeAccounting = initTaxAccounting(bookingCopy, bookingCopy.currency);
  }
  const taxAccountingEntries: IAccountingEntry[] =
    taxTypeAccounting.accountingEntriesList || [];
  // Create ae for every pss that doesn't have taxAcc.ae
  // const bookingLifeTimePssValues = BookingModule.bookingLifeTimePssValues;
  for (const pss of bookingCopy.pssList) {
    let hasTaxAccEntry = true;
    if (
      !taxTypeAccounting.accountingEntriesList ||
      !taxTypeAccounting.accountingEntriesList.length
    ) {
      hasTaxAccEntry = false;
    } else {
      const pssTaxAEIndex = taxTypeAccounting.accountingEntriesList.findIndex(
        (ae) => ae.productSlotStateUuid === pss.uuid
      );
      if (pssTaxAEIndex < 0) {
        hasTaxAccEntry = false;
      }
    }
    if (hasTaxAccEntry) {
      // this pss has already a tax accounting entry, we don't need to create a new one for it
      continue;
    }
    const pssAE: IAccountingEntry[] = createTaxAccountingEntries(
      pss,
      taxTypeAccounting /*,
      bookingLifeTimePssValues*/
    );
    if (!pssAE.length) continue;
    taxAccountingEntries.push(...pssAE);
  }
  if (!taxAccountingEntries.length) return;
  taxTypeAccounting.accountingEntriesList = taxAccountingEntries;

  const updatedBooking = Object.assign({}, bookingCopy);
  // all accountings except TAX that is not refund
  const withoutTaxAccountings = updatedBooking.accountings.filter(
    (acc) =>
      acc.transactionType !== TransactionTypes.TRANSACTION_TYPE_TAX ||
      (acc.transactionType === TransactionTypes.TRANSACTION_TYPE_TAX &&
        acc.refund === 1) /* i.e: created when cancellation */
  );
  // update the booking after updating the TAX acc.
  withoutTaxAccountings.push(taxTypeAccounting);
  BookingModule.updateBooking(bookingCopy);
  BookingModule.updateBookingAccountings({
    bookingUUID: boookingUUID,
    accountings: withoutTaxAccountings,
  });
  LogService.info(
    `Done creating/updating TAX_POS type accounting for the booking: ${bookingCopy.uuid}`,
    {
      params: taxTypeAccounting,
    }
  );
}

/**
 * this function creates an returns TAX accounting entries with positive values for one canceled pss
 * @param pss: the canceled pss
 * @param accounting of type IAccounting: the TAX accounting object that has refund=1
 */
export function createCancelPSSTaxAccountingEntries(
  pss: IProductSlotState,
  taxAccounting: IAccounting
): IAccountingEntry[] {
  // const bookingLifeTimePssValues = BookingModule.bookingLifeTimePssValues;
  const cancellationAccEntries = createTaxAccountingEntries(
    pss,
    taxAccounting /*,
    bookingLifeTimePssValues*/
  );

  // This is a paid pss cancellation, so the accEntry value should be positive
  for (let i = 0; i < cancellationAccEntries.length; i++) {
    cancellationAccEntries[i].value = Math.abs(cancellationAccEntries[i].value);
  }

  return cancellationAccEntries;
}

/**
 * Remove an accounting from the booking by its uuid
 */
export function removeAccounting(uuid: string) {
  const booking = BookingModule.selectedBooking;
  if (!booking) {
    throw new Error(PSBErrors.NoBookingInModule);
  }
  const updatedBooking = Object.assign({}, booking);
  // remove only one accounting by its uuid
  const updatedAccountingArray = updatedBooking.accountings;

  const index = updatedAccountingArray.findIndex((acc) => acc.uuid === uuid);
  if (index > -1) {
    updatedAccountingArray.splice(index, 1);
  }
  updatedBooking.accountings = updatedAccountingArray;
  BookingModule.updateBooking(updatedBooking);
  LogService.info(
    `Done removing booking accounting with uuid:${uuid}, booking: ${booking?.uuid}`
  );
}

/**
 * returns an accounting of type promocode/discount related to one pss that is cancelled, with negative value and refund:1
 * knowing that the pss could be paid using a code (case of voucher/giftcard) or it just could be discounted using the code
 */
export function createCancelPSSDiscountAccounting(
  booking: IProductSlotBooking,
  pssList: IProductSlotState[],
  accounting: IAccounting
): IAccounting | null {
  console.log(
    "------------ hi from createCancelPSSDiscountAccounting ------------"
  );
  LogService.info(
    `Going to create cancellation promocode/discount accounting for attached pss: ${pssList.map(
      (pss) => pss.uuid
    )}, booking: ${booking.uuid}`,
    {
      params: pssList,
    }
  );
  const uuid = generateID();
  const creationTime = dayjs().toISOString();
  const accountingState = AccountingState.Completed;
  const transactionType = accounting.transactionType;
  const paymentMethod = accounting.paymentMethod;
  const promoCodeId = accounting.promoCodeId;
  const promocode = accounting.promocode;

  const cancellationAccounting: IAccounting = {
    _id: uuid,
    uuid: uuid,
    orderSessionId: String(BookingModule.currentOrderSessionId),
    state: accountingState,
    transactionType: transactionType,
    paymentMethod: paymentMethod,
    bookingUuid: booking.uuid,
    promoCodeId: promoCodeId,
    promoCodeUuid: promocode,
    createdAt: creationTime,
    updatedAt: creationTime,
    message: creationTime + " :: accounting was created.",
    isDeposit: false,
    currency: booking.currency,
    accountingEntriesList: [],
    cashRegisterId: DeviceModule.cashRegisterId,
    channel: "Smeetz POS",
    merchantReference: "",
    CfUid: CashFloatModule.cashFloat?.uid,
    createdBy: AuthModule.cognitoId,
    promocode: promocode,
    refund: 1,
    originalAccountingUuid: accounting.uuid,
  };

  // if (
  //   cancellationAccounting.transactionType ===
  //   TransactionTypes.TRANSACTION_TYPE_PAYMENT
  // ) {
  //   // so this was a payment code not a discount
  //   createPromocodePaymentAccountingEntries(cancellationAccounting, [pss]);
  //   // value should be negative
  //   cancellationAccounting.accountingEntriesList[0].value *= -1;
  // } else {
  //   // so this was a discount code not a payment
  //   createPromocodeDiscountAccountingEntries(cancellationAccounting, [pss]);
  //   // value should be negative
  //   cancellationAccounting.accountingEntriesList[0].value *= -1;
  // }
  /////////////////////////
  for (let i = 0; i < pssList.length; i++) {
    const pss = pssList[i];
    const now = nowDB();
    const correspondingPaymentAccEntry = accounting.accountingEntriesList.find(
      (ae) => ae.productSlotStateUuid === pss.uuid
    );
    let refundValue = 0;
    if (correspondingPaymentAccEntry) {
      refundValue = -1 * correspondingPaymentAccEntry.value;
    }
    const accountingEntry: IAccountingEntry = {
      _id: generateID(),
      uuid: generateID(),
      accountingUuid: cancellationAccounting.uuid,
      orderSessionId: String(BookingModule.currentOrderSessionId),
      productSlotStateUuid: pss.uuid,
      value: refundValue,
      groupId: Number(pss.productOwnerGroupId),
      bookingStatementVersion: 2,
      createdAt: now,
      updatedAt: now,
      productId: pss.productId,
    };

    if (correspondingPaymentAccEntry?.salesTaxMicroValue) {
      accountingEntry.salesTaxMicroValue =
        -1 * correspondingPaymentAccEntry.salesTaxMicroValue;
      accountingEntry.salesTaxIncluded =
        correspondingPaymentAccEntry.salesTaxIncluded;
      accountingEntry.salesTaxRate = correspondingPaymentAccEntry.salesTaxRate;
      accountingEntry.taxId = correspondingPaymentAccEntry.taxId;
    }

    if (pss && pss.id) {
      accountingEntry.productSlotStateId = pss.id;
    }
    if (accountingEntry.value !== 0) {
      // nothing was discounted on this ticket, so there is nothing to refund
      cancellationAccounting.accountingEntriesList.push(accountingEntry);
    }
  }

  if (!cancellationAccounting.accountingEntriesList.length) {
    LogService.info(
      `No cancellation promocode/discount entries for pss: ${pssList.map(
        (pss) => pss.uuid
      )}, so no accounting is created`
    );
    return null;
  }
  /////////////////////////
  LogService.info(
    `Done creating cancellation promocode/discount accounting for the pss ${pssList.map(
      (pss) => pss.uuid
    )}`,
    {
      params: cancellationAccounting,
    }
  );
  return cancellationAccounting;
}

function translatePaymentMethods(paymentMethod: string) {
  switch (paymentMethod) {
    case "CASH":
      return t("settings.payment-methods.cash");
    case "POS_CARD":
      return t("payment-method.CARD");
    case "INVOICE":
      return t("payment-method.INVOICE");
    case "TO_BE_INVOICED":
      return t("payment-method.TO_BE_INVOICED");
    case "BANK_TRANSFER":
      return t("payment-method.BANK_TRANSFER");
    case "UNKNOWN":
      return t("payment-method.UNKNOWN");
    case "POS_ADYEN":
      return t("payment-method.POS_ADYEN");
    // case "DISCOUNT":
    //   return t("button.discount");
    case "POS_DISCOUNT":
      return t("payment-method.POS_DISCOUNT");
    case "FREE":
      return t("payment-method.FREE");
    case "CHEQUE":
      return t("payment-method.CHEQUE");
    case "CHEQUE_VACANCES":
      return t("payment-method.CHEQUE_VACANCES");
    case "CHEQUE_CULTURE":
      return t("payment-method.CHEQUE_CULTURE");
    case "PASS_CULTURE":
      return t("payment-method.PASS_CULTURE");
    case "CHORUS":
      return t("payment-method.CHORUS");
    // case "CE":
    //   return t("button.CE");
    // case "OCCYG":
    //   return t("button.OCCYG");
    case "POS_PROMO_CODE":
      return t("payment-method.POS_PROMO_CODE");
    case "POS_VOUCHER":
      return t("payment-method.VOUCHER");
    case "GIFT_CARD":
      return t("payment-method.GIFT_CARD");
    case "POS_MEMBERSHIP":
      return t("payment-method.POS_MEMBERSHIP");
    case "AMEX":
      return t("payment-method.AMEX");
    case "CE":
      return t("payment-method.CE");
    case "COUPON_SPORT":
      return t("payment-method.COUPON_SPORT");
    case "DISCOUNT":
      return t("payment-method.DISCOUNT");
    case "EXPERIENCEBANK":
      return t("payment-method.EXPERIENCEBANK");
    case "EXTERNAL_VOUCHER":
      return t("payment-method.EXTERNAL_VOUCHER");
    case "invitation":
      return t("payment-method.invitation");
    case "KIOSK_ADYEN":
      return t("payment-method.KIOSK_ADYEN");
    case "MASTERCARD":
      return t("payment-method.MASTERCARD");
    case "MEMBERSHIP":
      return t("payment-method.MEMBERSHIP");
    case "OCCYG":
      return t("payment-method.OCCYG");
    case "PAYPAL":
      return t("payment-method.PAYPAL");
    case "POS_ADYEN_CREATE_ORDER":
      return t("payment-method.POS_ADYEN_CREATE_ORDER");
    case "POS_BANK_TRANSFER":
      return t("payment-method.POS_BANK_TRANSFER");
    // case "POS_CARD":
    // return t("payment-method.POS_CARD");
    case "POS_CASH":
      return t("payment-method.POS_CASH");
    case "POS_EXTERNAL":
      return t("payment-method.POS_EXTERNAL");
    case "POS_GIFT_CARD":
      return t("payment-method.POS_GIFT_CARD");
    case "POS_INVITATION":
      return t("payment-method.POS_INVITATION");
    case "POS_INVOICE":
      return t("payment-method.POS_INVOICE");
    case "POS_TWINT":
      return t("payment-method.POS_TWINT");
    case "POSTCARD":
      return t("payment-method.POSTCARD");
    case "POSTEFINANCE":
      return t("payment-method.POSTEFINANCE");
    case "PROMO_CODE":
      return t("payment-method.PROMO_CODE");
    case "QOQA_VOUCHER":
      return t("payment-method.QOQA_VOUCHER");
    case "REFUNDED_VOUCHER":
      return t("payment-method.REFUNDED_VOUCHER");
    case "SPONSORED":
      return t("payment-method.SPONSORED");
    case "STAND_ALONE_ADYEN":
      return t("payment-method.STAND_ALONE_ADYEN");
    case "TWINT":
      return t("payment-method.TWINT");
    case "VISA":
      return t("payment-method.VISA");
    case "POS_ADYEN_PAY_BY_LINK":
      return t("payment-method.POS_ADYEN_PAY_BY_LINK");
    case "POS_REKA":
      return t("payment-method.POS_REKA");
    case "POS_DIVERSE_VOUCHER":
      return t("payment-method.POS_DIVERSE_VOUCHER");

    case "CFF":
      return t("payment-method.cff");
    case "CGN":
      return t("payment-method.cgn");
    case "QOQA":
      return t("payment-method.qoqa");
    case "DEINDEAL":
      return t("payment-method.deindeal");
    case "REALDEAL":
      return t("payment-method.realdeal");
    case "EXTERNAL_INCERT_VOUCHER":
      return t("payment-method.Incert_Voucher");
  }
}

export function getCurrencySymbol(currency: Currencies) {
  switch (currency) {
    case Currencies.CHF:
      return "₣";
    case Currencies.EUR:
      return "€";
    case Currencies.GBP:
      return "£";
    case Currencies.USD:
      return "$";
    default:
      return currency;
  }
}
